import { ORDER_STATUS_TONE, type OrderStatus, type UserRef } from '@ft/shared';
import { useI18n } from '../i18n';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { custColor, pct } from '../lib/format';
import { api } from '../lib/http';
import { Avatar } from '../lib/people';

export function Cust({ name }: { name: string }) {
  return <span className="cust"><i className="sw" style={{ background: custColor(name) }} />{name}</span>;
}

export function PmChips({ pms }: { pms: UserRef[] }) {
  if (!pms.length) return <span className="muted">–</span>;
  return <span className="pmstack">{pms.map((p) => <Avatar key={p.id} id={p.id} name={p.name} initials={p.initials} />)}</span>;
}

export function StatusPill({ s }: { s: OrderStatus }) {
  const { t } = useI18n();
  return <span className={`pill ${ORDER_STATUS_TONE[s]}`}>{t(`status.${s}`)}</span>;
}

export function Use({ volume, billed }: { volume: number; billed: number }) {
  const u = volume > 0 ? billed / volume : 0;
  const col = u > 1.005 ? 'var(--neg)' : u >= 0.85 ? 'var(--crit)' : 'var(--neu-bar)';
  return (
    <div className="use">
      <div className="meter"><i style={{ width: `${Math.min(100, u * 100)}%`, background: col }} /></div>
      <span className="num">{volume > 0 ? pct(u) : '–'}</span>
    </div>
  );
}

export function Modal({ title, crumb, onClose, children, footer, width }: { title: string; crumb?: string; onClose: () => void; children: ReactNode; footer: ReactNode; width?: number }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input,select,textarea')?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('keydown', key); prev?.focus(); };
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal">
        <div className="modal-card" role="dialog" aria-modal="true" aria-label={title} ref={ref} style={width ? { width: `min(${width}px, 100%)` } : undefined}>
          <div className="modal-h">
            <div>{crumb && <div className="crumb">{crumb}</div>}<h3>{title}</h3></div>
            <button className="x" onClick={onClose} aria-label={t('common.close')}>×</button>
          </div>
          {children}
          <div className="modal-f">{footer}</div>
        </div>
      </div>
    </>
  );
}

/** Kurze Rückmeldung unten in der Mitte. */
/** Kurze Meldung unten; mit Link (ReactNode) bleibt sie etwas länger stehen. */
export function useToast(): [ReactNode, (msg: ReactNode) => void] {
  const [msg, setMsg] = useState<ReactNode>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), typeof msg === 'string' ? 3200 : 7000);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg ? <div className="toast" role="status">{msg}</div> : null, setMsg];
}

/** Lädt Daten per GET; reload() lädt neu. */
export function useLoad<T>(path: string | null): { data: T | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    setError(null);
    api<T>('GET', path).then((d) => { if (alive) setData(d); }, (e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [path, n]);
  const reload = useCallback(() => setN((x) => x + 1), []);
  return { data, error, reload };
}

export function Loading({ error }: { error: string | null }) {
  const { t } = useI18n();
  return error ? <div className="box empty"><p className="err">{error}</p></div> : <div className="box empty">{t('app.loading')}</div>;
}
